import React, { useState } from "react";
import { useShow, useList, useDataProvider, useDelete } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import {
	Card,
	Typography,
	Row,
	Col,
	Button,
	Tag,
	Descriptions,
	Checkbox,
	Modal,
	Space,
	message,
} from "antd";
import { RollbackOutlined, DownloadOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { ApplicationList } from "../../components/races/ApplicationList";
import { ApplicationDetail } from "../../components/races/ApplicationDetail";
import ExcelJS from 'exceljs';
import { RaceMaster, RaceApplicationDTO, RaceCrewDTO, RatingDTO } from "../../types/race/models";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

const { Title } = Typography;

export const RaceShow: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const dataProvider = useDataProvider();
	const { mutate: deleteRace } = useDelete();
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);

	// 編集・削除機能
	const handleEdit = () => {
		navigate(`/races/edit/${id}`);
	};

	const showDeleteConfirm = () => {
		setDeleteModalVisible(true);
	};

	const handleDeleteCancel = () => {
		setDeleteModalVisible(false);
	};

	const handleDelete = () => {
		deleteRace(
			{
				resource: "race_master",
				id: id as string,
			},
			{
				onSuccess: () => {
					message.success("レースを削除しました");
					navigate("/races");
				},
				onError: (error) => {
					message.error("レースの削除に失敗しました");
					console.error("Error deleting race:", error);
				},
			}
		);
	};

	// レースマスターデータ取得
	console.log('Current ID:', id);

	const { queryResult } = useShow<RaceMaster>({
		resource: "race_master",
		id: id || "",
		queryOptions: {
			enabled: !!id,
			retry: 1,
			onError: (error) => {
				console.error('Error fetching race master:', error);
			},
		},
	});
	const { data, isLoading } = queryResult;
	const record = data?.data;

	console.log('Race Master Data:', { isLoading, data, record });

	// 申込データ取得
	const [selectedApplication, setSelectedApplication] = useState<string | null>(null);

	const { data: applicationsData, isLoading: isLoadingApplications } = useList<RaceApplicationDTO>({
		resource: "race_applications",
		filters: [
			{
				field: "race_master_id",
				operator: "eq",
				value: id || "",
			},
		],
		pagination: {
			pageSize: 50,
		},
		sorters: [
			{
				field: "created_at",
				order: "desc"
			}
		],
		queryOptions: {
			enabled: !!id,
			retry: 1,
			onError: (error) => {
				console.error('Error fetching applications:', error);
			},
		},
	});

	console.log('Applications Data:', { isLoadingApplications, applicationsData });

	const applications = applicationsData?.data ?? [];

	if (isLoading || isLoadingApplications) {
		return <div>Loading...</div>;
	}

	if (!record) {
		return <div>データが見つかりません</div>;
	}

	// クラス名の変換関数
	const convertClassName = (className: string): string => {
		switch (className) {
			case 'irc': return 'IRCクラス';
			case 'kyc': return 'KYCレーティングクラス';
			case 'middle': return 'ミドルボートクラス';
			default: return className;
		}
	};

	const handleExportApplicantsSummary = async () => {
		if (!record) return;

		// 完了済みの申込のみを取得
		const completedApplications = applications.filter(app => app.status === 'completed');

		// クルー情報とレーティング情報を取得
		const responses = await Promise.all([
			dataProvider().getList<RaceCrewDTO>({
				resource: "race_crews",
				filters: [
					{
						field: "race_application_id",
						operator: "in",
						value: completedApplications.map(app => app.id)
					}
				],
				pagination: { current: 1, pageSize: 1000 }
			}),
			dataProvider().getList<RatingDTO>({
				resource: "ratings",
				filters: [
					{
						field: "race_application_id",
						operator: "in",
						value: completedApplications.map(app => app.id)
					}
				],
				pagination: { current: 1, pageSize: 1000 }
			})
		]);

		const crewsResponse = responses[0];
		const ratingsResponse = responses[1];

		const { data: crews } = crewsResponse;
		const { data: ratings } = ratingsResponse;

		const workbook = new ExcelJS.Workbook();
		const sheet = workbook.addWorksheet('申込者一覧');

		// デフォルトの設定
		const defaultAlignment: Partial<ExcelJS.Alignment> = { vertical: 'middle', horizontal: 'left' };

		// すべての行の高さを設定
		for (let i = 1; i <= completedApplications.length * 18 + 1; i++) {
			const row = sheet.getRow(i);
			row.height = 20;
			row.eachCell((cell) => {
				if (!cell.font) {
					cell.font = { name: 'Yu Gothic', size: 11 };
				}
				cell.alignment = defaultAlignment;
			});
		}

		// レース名を最初に1回だけ設定
		const a1Cell = sheet.getCell('A1');
		a1Cell.value = record.name;
		a1Cell.font = { name: 'Yu Gothic', size: 16, bold: true };
		a1Cell.alignment = { ...defaultAlignment, horizontal: 'left' };

		let currentRow = 2;

		// 申込ごとにデータを追加
		for (const app of completedApplications) {
			// 艇長（スキッパー）の情報を取得
			const skipper = crews.find(crew =>
				crew.race_application_id === app.id && crew.is_skipper
			);

			// 艇名
			sheet.getCell(`A${currentRow}`).value = '艇　名';
			sheet.getCell(`B${currentRow}`).value = app.yacht_name;
			sheet.getCell(`C${currentRow}`).value = '艇　種';
			sheet.getCell(`D${currentRow}`).value = app.yacht_type;
			currentRow++;

			// セール番号と定係港
			sheet.getCell(`A${currentRow}`).value = 'セールＮｏ';
			sheet.getCell(`B${currentRow}`).value = app.sail_number;
			sheet.getCell(`C${currentRow}`).value = '定係港';
			sheet.getCell(`D${currentRow}`).value = app.home_port;
			currentRow++;

			// 全長
			sheet.getCell(`A${currentRow}`).value = '全　長';
			sheet.getCell(`B${currentRow}`).value = `${app.yacht_length} フィート`;
			// C4,D4を結合してグレーに
			sheet.mergeCells(`C${currentRow}:D${currentRow}`);
			const mergedCell = sheet.getCell(`C${currentRow}`);
			mergedCell.fill = {
				type: 'pattern',
				pattern: 'solid',
				fgColor: { argb: 'FFE0E0E0' }
			};
			currentRow++;

			// オーナー
			sheet.mergeCells(`A${currentRow}:D${currentRow}`);
			const ownerHeaderCell = sheet.getCell(`A${currentRow}`);
			ownerHeaderCell.value = 'オ　ー　ナ　ー';
			ownerHeaderCell.font = {
				name: 'Yu Gothic',
				size: 13,
				bold: true
			};
			ownerHeaderCell.alignment = defaultAlignment;
			ownerHeaderCell.border = {
				top: { style: 'thin' },
				bottom: { style: 'thin' }
			};
			currentRow++;

			sheet.getCell(`A${currentRow}`).value = '氏  名';
			sheet.getCell(`B${currentRow}`).value = `${app.owner_last_name} ${app.owner_first_name}`;
			sheet.getCell(`C${currentRow}`).value = '住  所';
			sheet.getCell(`D${currentRow}`).value = `${app.owner_postal_code} ${app.owner_prefecture}${app.owner_city}${app.owner_street}${app.owner_building || ''}`;
			currentRow++;

			sheet.getCell(`A${currentRow}`).value = '携帯電話';
			sheet.getCell(`B${currentRow}`).value = app.owner_phone;
			sheet.getCell(`C${currentRow}`).value = 'E-mail';
			sheet.getCell(`D${currentRow}`).value = app.owner_email;
			currentRow++;

			// 緊急連絡先情報を追加
			sheet.getCell(`A${currentRow}`).value = '緊急連絡先氏名';
			sheet.getCell(`B${currentRow}`).value = app.owner_emergency_contact_name;
			sheet.getCell(`C${currentRow}`).value = '電話番号';
			sheet.getCell(`D${currentRow}`).value = app.owner_emergency_contact_phone;
			currentRow++;

			// 艇長
			sheet.mergeCells(`A${currentRow}:D${currentRow}`);
			const skipperHeaderCell = sheet.getCell(`A${currentRow}`);
			skipperHeaderCell.value = '艇　長';
			skipperHeaderCell.font = {
				name: 'Yu Gothic',
				size: 13,
				bold: true
			};
			skipperHeaderCell.alignment = defaultAlignment;
			skipperHeaderCell.border = {
				top: { style: 'thin' },
				bottom: { style: 'thin' }
			};
			currentRow++;

			if (skipper) {
				sheet.getCell(`A${currentRow}`).value = '氏  名';
				sheet.getCell(`B${currentRow}`).value = `${skipper.last_name} ${skipper.first_name}`;
				sheet.getCell(`C${currentRow}`).value = '住  所';
				sheet.getCell(`D${currentRow}`).value = `${skipper.postal_code} ${skipper.prefecture}${skipper.city}${skipper.street}${skipper.building || ''}`;
				currentRow++;

				sheet.getCell(`A${currentRow}`).value = '携帯電話';
				sheet.getCell(`B${currentRow}`).value = skipper.phone;
				sheet.getCell(`C${currentRow}`).value = 'E-mail';
				sheet.getCell(`D${currentRow}`).value = skipper.email;
				currentRow++;
			} else {
				// 艇長情報がない場合は空欄を追加
				currentRow += 2;
			}

			// 海上緊急通信手段
			sheet.mergeCells(`A${currentRow}:D${currentRow}`);
			const emergencyHeaderCell = sheet.getCell(`A${currentRow}`);
			emergencyHeaderCell.value = '海上緊急通信手段　（オーナー、艇長または通信担当者氏名/携帯番号）';
			emergencyHeaderCell.font = {
				name: 'Yu Gothic',
				size: 13,
				bold: true
			};
			emergencyHeaderCell.alignment = defaultAlignment;
			emergencyHeaderCell.border = {
				top: { style: 'thin' },
				bottom: { style: 'thin' }
			};
			currentRow++;

			sheet.getCell(`A${currentRow}`).value = 'VHF';
			sheet.getCell(`B${currentRow}`).value = app.has_vhf_radio ? '☑' : '';
			sheet.getCell(`C${currentRow}`).value = 'コールサイン';
			sheet.getCell(`D${currentRow}`).value = app.call_sign;
			currentRow++;

			sheet.getCell(`A${currentRow}`).value = '氏  名';
			sheet.getCell(`B${currentRow}`).value = app.liaison_name;
			sheet.getCell(`C${currentRow}`).value = '携帯電話';
			sheet.getCell(`D${currentRow}`).value = app.liaison_phone;
			currentRow++;

			// 参加クラス
			sheet.getCell(`A${currentRow}`).value = '参加クラス';
			sheet.getCell(`B${currentRow}`).value = app.participating_classes
				.map((className: string) => convertClassName(className))
				.join(', ');
			currentRow++;

			// 乗員数
			sheet.getCell(`A${currentRow}`).value = '乗員数';
			sheet.getCell(`B${currentRow}`).value = app.max_crew_count;
			currentRow++;

			// レーティング情報を追加
			sheet.mergeCells(`A${currentRow}:D${currentRow}`);
			const ratingHeaderCell = sheet.getCell(`A${currentRow}`);
			ratingHeaderCell.value = 'レーティング';
			ratingHeaderCell.font = {
				name: 'Yu Gothic',
				size: 13,
				bold: true
			};
			ratingHeaderCell.alignment = defaultAlignment;
			ratingHeaderCell.border = {
				top: { style: 'thin' },
				bottom: { style: 'thin' }
			};
			currentRow++;

			// レーティング情報をカンマ区切りで表示
			const rating = ratings.find(r => r.race_application_id === app.id);
			if (rating) {
				const ratingItems = [];
				if (rating.has_dacron_main === true) ratingItems.push('ダクロンメイン');
				if (rating.has_main_furler === true) ratingItems.push('メインファーラー');
				if (rating.has_boom_furler === true) ratingItems.push('ブームファーラー');
				if (rating.has_dacron_jib === true) ratingItems.push('ダクロンジブ');
				if (rating.has_jib_furler === true) ratingItems.push('ジブファーラー');
				if (rating.has_solid_spade === true) ratingItems.push('ソリッドペラ');
				if (rating.has_non_pole === true) ratingItems.push('ノンポール');
				if (rating.has_double_handle === true) ratingItems.push('ダブルハンド');
				if (rating.has_mooring === true) ratingItems.push('水上係留艇');
				if (rating.has_electric_winch === true) ratingItems.push('電動ウインチ');
				if (rating.spinnaker_type === 'non_spinnaker') ratingItems.push('ノンスピン/ノンジェネカー');
				if (rating.spinnaker_type === 'above_eye_point') ratingItems.push('アイポイントを超える位置のスピン/ジェネカー');
				if (rating.spinnaker_type === 'fractional') ratingItems.push('フラクナショナル');

				sheet.mergeCells(`A${currentRow}:D${currentRow}`);
				const ratingCell = sheet.getCell(`A${currentRow}`);
				ratingCell.value = ratingItems.join(', ') || '-';
				ratingCell.alignment = {
					...defaultAlignment,
					wrapText: true, // 折り返しを有効に
					vertical: 'top' // 上揃えにする
				};
				// 長い値の場合は高さを40に設定
				const row = sheet.getRow(currentRow);
				row.height = ratingItems.join(', ').length > 50 ? 40 : 20;
			} else {
				sheet.mergeCells(`A${currentRow}:D${currentRow}`);
				const ratingCell = sheet.getCell(`A${currentRow}`);
				ratingCell.value = '-';
				ratingCell.alignment = defaultAlignment;
				sheet.getRow(currentRow).height = 20;
			}
			currentRow++;


			// 空行を追加
			currentRow++;
		}

		// 列幅の設定
		sheet.getColumn('A').width = 20;
		sheet.getColumn('B').width = 30;
		sheet.getColumn('C').width = 15;
		sheet.getColumn('D').width = 50;

		// セルのスタイルを確実に適用
		sheet.eachRow((row) => {
			row.eachCell((cell) => {
				if (!cell.font) {
					cell.font = { name: 'Yu Gothic', size: 11 };
				}
				if (!cell.alignment) {
					cell.alignment = defaultAlignment;
				}
			});
		});

		// 各レコードの外枠を太い罫線で囲む
		completedApplications.forEach((_, index) => {
			const startRow = 2 + (index * 18); // 各申込み18行分（内容17行 + 空行1行）
			const contentEndRow = startRow + 16; // 内容の最終行
			const emptyRow = startRow + 17; // 空行

			// 内容部分の枠線
			['A', 'B', 'C', 'D'].forEach(col => {
				// 上下の罫線
				sheet.getCell(`${col}${startRow}`).border = {
					...sheet.getCell(`${col}${startRow}`).border,
					top: { style: 'thick' }
				};
				// 内容の最終行には下線なし
			});

			// 左右の罫線（内容部分のみ）
			for (let row = startRow; row <= contentEndRow; row++) {
				sheet.getCell(`A${row}`).border = {
					...sheet.getCell(`A${row}`).border,
					left: { style: 'thick' }
				};
				sheet.getCell(`D${row}`).border = {
					...sheet.getCell(`D${row}`).border,
					right: { style: 'thick' }
				};
			}

			// 空行の上下の線
			['A', 'B', 'C', 'D'].forEach(col => {
				sheet.getCell(`${col}${emptyRow}`).border = {
					top: { style: 'thick' },
					bottom: { style: 'thick' }
				};
			});
		});

		try {
			// ファイルの保存
			const buffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `${record.name}_applicants-summary.xlsx`;

			// ファイルのダウンロードが完了するまで待つ
			await new Promise(resolve => {
				link.onclick = () => {
					setTimeout(resolve, 1000);
					return true;
				};
				link.click();
				setTimeout(resolve, 1000); // クリックが発生しなかった場合のフォールバック
			});

			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Excelファイルの生成中にエラーが発生しました:', error);
		}
	};

	const handleExportExcel = async () => {
		if (!record) return;

		// 完了済みの申込のみを取得
		const completedApplications = applications.filter(app => app.status === 'completed');

		try {
			// クルー情報を取得
			const { data: crews } = await dataProvider().getList<RaceCrewDTO>({
				resource: "race_crews",
				filters: [
					{
						field: "race_application_id",
						operator: "in",
						value: completedApplications.map(app => app.id)
					}
				],
				pagination: { current: 1, pageSize: 1000 }
			});

			// クルー情報を申込IDごとにグループ化
			const crewsByApplication = completedApplications.map(app =>
				crews.filter((crew: RaceCrewDTO) => crew.race_application_id === app.id)
			);

			const workbook = new ExcelJS.Workbook();
			const sheet = workbook.addWorksheet('保険会社提出用（レース参加者一覧）');

			// デフォルトの行の高さ設定
			sheet.properties.defaultRowHeight = 20;

			// ヘッダー部分の設定
			const a1Cell = sheet.getCell('A1');
			a1Cell.value = record.name;
			a1Cell.font = {
				name: 'Yu Gothic',
				size: 20,
				bold: true
			};

			// 列ヘッダー
			const headerRow = sheet.getRow(2);
			headerRow.values = ['No.', null, '艇名', '氏名', '読み', '年齢', '性別', '合計乗員数'];
			headerRow.font = {
				name: 'Yu Gothic',
				bold: true
			};

			// ヘッダー行の罫線設定
			['A2', 'B2', 'C2', 'D2', 'E2', 'F2', 'G2', 'H2'].forEach(cell => {
				const headerCell = sheet.getCell(cell);
				headerCell.border = {
					top: { style: 'thin', color: { argb: 'FF000000' } },
					left: { style: 'thin', color: { argb: 'FF000000' } },
					bottom: { style: 'thin', color: { argb: 'FF000000' } },
					right: { style: 'thin', color: { argb: 'FF000000' } }
				};
			});

			// 列幅の設定
			sheet.getColumn('A').width = 5;   // No.
			sheet.getColumn('B').width = 5;   // 艇No.
			sheet.getColumn('C').width = 22;  // 艇名
			sheet.getColumn('D').width = 30;  // 氏名
			sheet.getColumn('E').width = 30;  // 読み
			sheet.getColumn('F').width = 6;   // 年齢
			sheet.getColumn('G').width = 6;   // 性別
			sheet.getColumn('H').width = 12;  // 合計乗員数

			// F列、G列、H列を中央揃えに設定
			sheet.getColumn('F').alignment = { horizontal: 'center' };
			sheet.getColumn('G').alignment = { horizontal: 'center' };
			sheet.getColumn('H').alignment = { horizontal: 'center' };

			let currentRow = 3;
			let sequentialNo = 1;

			// 申込ごとにデータを追加
			completedApplications.forEach((app, appIndex) => {
				const crews = crewsByApplication[appIndex];

				crews.forEach((crew, crewIndex) => {
					const row = sheet.getRow(currentRow);

					// No.
					row.getCell(1).value = sequentialNo;

					// 艇No.（最初の行のみ）
					if (crewIndex === 0) {
						row.getCell(2).value = appIndex + 1;
					}

					// 艇名（最初の行のみ）
					if (crewIndex === 0) {
						row.getCell(3).value = app.yacht_name;
					}

					// クルー情報
					row.getCell(4).value = `${crew.last_name} ${crew.first_name}`;
					row.getCell(5).value = `${crew.last_name_kana} ${crew.first_name_kana}`;

					// 年齢計算
					const birthDate = new Date(crew.date_of_birth);
					const today = new Date();
					let age = today.getFullYear() - birthDate.getFullYear();
					const monthDiff = today.getMonth() - birthDate.getMonth();
					if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
						age--;
					}
					row.getCell(6).value = age;

					// 性別
					row.getCell(7).value = crew.gender;

					// 合計乗員数（最初の行のみ）
					if (crewIndex === 0) {
						row.getCell(8).value = crews.length;
					}

					// フォント設定
					row.font = { name: 'Yu Gothic' };

					// 罫線設定
					['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].forEach(col => {
						const cell = row.getCell(col);
						cell.border = {
							top: { style: 'thin', color: { argb: 'FF000000' } },
							left: { style: 'thin', color: { argb: 'FF000000' } },
							bottom: { style: 'thin', color: { argb: 'FF000000' } },
							right: { style: 'thin', color: { argb: 'FF000000' } }
						};
					});

					currentRow++;
					sequentialNo++;
				});
			});

			// ファイルの保存
			const buffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `${record.name}_race-applicants.xlsx`;
			link.click();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error exporting Excel:', error);
		}
	};

	return (
		<div className="space-y-4">
			<Card
				title={
					<Row justify="space-between" align="middle">
						<Col>
							<Title level={3}>レース詳細</Title>
						</Col>
						<Col>
							<Space>
								<Button
									icon={<RollbackOutlined />}
									onClick={() => navigate("/races")}
								>
									一覧に戻る
								</Button>
								<Button
									type="primary"
									icon={<EditOutlined />}
									onClick={handleEdit}
								>
									編集
								</Button>
								<Button
									danger
									icon={<DeleteOutlined />}
									onClick={showDeleteConfirm}
								>
									削除
								</Button>
							</Space>
						</Col>
					</Row>
				}
			>
				<Descriptions bordered>
					<Descriptions.Item label="レース名" span={3}>
						{record?.name}
					</Descriptions.Item>
					<Descriptions.Item label="開催日" span={3}>
						{record?.race_date ? dayjs(record.race_date).format("YYYY年MM月DD日") : "-"}
					</Descriptions.Item>
					<Descriptions.Item label="申込締切" span={3}>
						{record?.deadline_date ? dayjs(record.deadline_date).format("YYYY年MM月DD日") : "-"}
					</Descriptions.Item>
					<Descriptions.Item label="公開日時" span={3}>
						{record?.publication_date_time ? dayjs(record.publication_date_time).format("YYYY年MM月DD日 HH:mm") : "-"}
					</Descriptions.Item>
					<Descriptions.Item label="レースタイプ" span={3}>
						{record?.is_dragon ? "ドラゴンレース" : "通常レース"}
					</Descriptions.Item>
					<Descriptions.Item label="複数日開催" span={3}>
						<Checkbox checked={record?.is_multiple} disabled />
					</Descriptions.Item>
					<Descriptions.Item label="公開状態" span={3}>
						{record?.publication_status === "draft" && <Tag color="processing">下書き</Tag>}
						{record?.publication_status === "published" && <Tag color="success">公開中</Tag>}
					</Descriptions.Item>
					<Descriptions.Item label="レースステータス" span={3}>
						{record?.status === "draft" && <Tag color="default">下書き</Tag>}
						{record?.status === "published" && <Tag color="processing">公開中</Tag>}
						{record?.status === "closed" && <Tag color="success">終了</Tag>}
						{record?.status === "cancelled" && <Tag color="error">中止</Tag>}
					</Descriptions.Item>
				</Descriptions>

			</Card>

			<Card>
				<Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
					<Col>
						<Title level={4} style={{ margin: 0 }}>申込一覧</Title>
					</Col>
					<Col>
						<Button
							type="primary"
							onClick={handleExportApplicantsSummary}
							icon={<DownloadOutlined />}
						>
							申込データサマリーExcel
						</Button>
						<Button
							type="primary"
							onClick={handleExportExcel}
							icon={<DownloadOutlined />}
							style={{ marginLeft: 8 }}
						>
							レース参加者一覧Excel
						</Button>
					</Col>
				</Row>
				<ApplicationList
					raceId={id || ""}
					onShowDetail={(applicationId) => setSelectedApplication(applicationId)}
				/>
			</Card>

			<Modal
				title="レースを削除"
				open={deleteModalVisible}
				onOk={handleDelete}
				onCancel={handleDeleteCancel}
				okText="削除"
				cancelText="キャンセル"
				okButtonProps={{ danger: true }}
			>
				<Typography.Title level={5} style={{ marginBottom: 24 }}>
					{record?.name}
				</Typography.Title>
				<Typography.Text>
					このレースを削除してもよろしいですか？
				</Typography.Text>
				<Typography.Text type="warning" style={{ display: 'block', marginTop: 16 }}>
					この操作は取り消すことができません。
				</Typography.Text>
			</Modal>

			<ApplicationDetail
				applicationId={selectedApplication}
				onClose={() => setSelectedApplication(null)}
			/>
		</div>
	);
};
